import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { validEmailValidator } from '../../shared/directives/valid-email.directive';
import { ToastService } from '../../shared/modules/toast/toast.service';
import { Account } from '../../shared/sdk';
import { TitleService } from '../../shared/services/title.service';
import { AccountService } from '../account.service';
import { PasswordMatch } from '../register/password-match.validator';

@Component({
  selector: 'app-reset',
  templateUrl: './reset.component.html',
  styleUrls: ['./reset.component.styl'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetComponent implements OnInit {
  loading: boolean;
  linkSent = false;
  resetForm: FormGroup;
  changePasswordForm: FormGroup;
  sent: boolean;
  hasToken: boolean;
  token: string;
  private account: any;

  constructor(
    private fb: FormBuilder,
    private accountService: AccountService,
    private toastService: ToastService,
    private translate: TranslateService,
    private titleService: TitleService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    this.sent = false;
  }

  ngOnInit() {
    this.titleService.set(['Password reset']);
    const params = this.route.snapshot.queryParams;

    // Has token, setting new pass
    if (params.access_token) {
      this.hasToken = true;
      this.token = params.access_token;

      this.changePasswordForm = this.fb.group(
        {
          newPassword: ['', [Validators.required, Validators.minLength(8)]],
          newPassword2: ['', [Validators.required, Validators.minLength(8)]],
        },
        { validator: PasswordMatch },
      );
    } else {
      // No token, requesting reset
      this.account = new Account();

      this.resetForm = this.fb.group({
        email: [
          '',
          Validators.compose([Validators.required, validEmailValidator()]),
        ],
      });
    }
  }

  onChangePassword() {
    this.sent = true;

    if (this.changePasswordForm.valid) {
      this.loading = true;

      this.accountService
        .setPassword(
          this.changePasswordForm.get('newPassword').value,
          this.token,
        )
        .subscribe(
          () => {
            this.loading = false;
            this.toastService.success(
              this.translate.instant('Password changed successfully'),
            );
            this.cdr.markForCheck();
            this.router.navigate(['login']);
          },
          (err) => {
            this.loading = false;

            if (
              typeof err === 'object' &&
              (err.statusCode === 401 || err.code === 'AUTHORIZATION_REQUIRED')
            ) {
              this.toastService.warning(
                this.translate.instant('password-reset.unauthorized.message'),
              );

              this.hasToken = false;
              this.token = null;
            } else {
              this.toastService.error(
                this.translate.instant(
                  'There was a problem resetting the password, please contact support',
                ),
              );
            }

            this.cdr.markForCheck();
          },
        );
    }
  }

  onReset() {
    this.sent = true;

    if (this.resetForm.valid) {
      this.loading = true;
      this.account.email = this.resetForm.value.email;

      this.accountService.reset(this.account).subscribe(
        (res) => {
          this.loading = false;
          this.linkSent = true;
          this.cdr.markForCheck();
        },
        (err) => {
          this.loading = false;

          if (typeof err === 'object' && err.statusCode === 404) {
            this.toastService.warning(
              this.translate.instant('reset.not-found.toast-message'),
            );
          } else {
            this.toastService.error(
              this.translate.instant(
                'There was a problem resetting the password, please contact support',
              ),
            );
          }

          this.cdr.markForCheck();
        },
      );
    }
  }
}
